import { Button, ModalHeader } from '@fountain/fountain-ui-components';
import { Drawer, Grid, Typography } from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import { produce } from 'immer';
import React from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  FormBuilderProvider,
  QuestionDrawerProvider,
  useQuestionDrawerContextState,
} from './context';
import { useWorkflowReviewStage } from './hooks';
import { formBuilderMessages } from './messages';
import { QuestionnaireDisplay } from './QuestionnaireDisplay';
import { CreateEditQuestionForm } from './QuestionTypeForms/CreateEditQuestion/CreateEditQuestionForm';
import { DataField, ReviewStageDataField } from './schemas';
import { useFormBuilderStyles } from './styles';

const {
  header,
  addQuestionText,
  editQuestionText,
  emptyStateHeader,
  emptyStateSubText,
} = formBuilderMessages;

const InnerFormBuilder = () => {
  const { activeDataEntries, stage, setStage } = useWorkflowReviewStage();
  const classes = useFormBuilderStyles();
  const { isDrawerOpen, isEdit, setCreateQuestion, closeDrawer, value } =
    useQuestionDrawerContextState();
  const intl = useIntl();

  const modalHeaderText = isEdit ? editQuestionText : addQuestionText;

  const hasUnsavedQuestions = activeDataEntries.some(
    dataField => !dataField.id,
  );
  // accepts_nested_attributes_for processes existing records (with id) before new records (without id),
  // which scrambles the array order before save and prevents us from preserving the drag-and-drop order
  // when mixing saved and unsaved questions.
  const isDraggingEnabled = !hasUnsavedQuestions;

  const onDragEnd = ({ destination, source }: DropResult) => {
    if (!destination) {
      return;
    }

    if (destination.index === source.index) {
      return;
    }

    setStage(
      produce(stage, draftStage => {
        const questionnaire = draftStage.additional_info
          .review_questionnaire as unknown as ReviewStageDataField[];

        const [removed] = questionnaire.splice(source.index, 1);
        questionnaire.splice(destination.index, 0, removed);

        // Update ALL positions to ensure acts_as_list on the backend doesn't reorder things
        // acts_as_list uses 1-based positions
        questionnaire.forEach((question, index) => {
          // eslint-disable-next-line no-param-reassign
          question.position = index + 1;
        });
      }),
    );
  };
  return (
    <Grid container xs={12} direction="column">
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        classes={{
          paperAnchorRight: classes.drawer,
        }}
        onClose={closeDrawer}
      >
        {/* DrawerHeader doesn't have the built in close and they look pretty interchangeable */}
        <ModalHeader
          ariaLabelledBy={intl.formatMessage(modalHeaderText)}
          onClose={closeDrawer}
          showIcon={false}
          className={classes.drawerHeader}
        >
          <FormattedMessage {...modalHeaderText} />
        </ModalHeader>
        <FormBuilderProvider key={value}>
          <CreateEditQuestionForm />
        </FormBuilderProvider>
      </Drawer>
      <Grid item className={classes.container} alignItems="center">
        <Typography className={classes.header}>
          <FormattedMessage {...header} />
        </Typography>
        <Button
          type="secondary"
          size="small"
          onClick={setCreateQuestion}
          autoWidth
        >
          <Typography
            variant="body2"
            color="primary"
            className={classes.addQuesitonButtonText}
          >
            <Add className={classes.addIcon} />
            <FormattedMessage {...addQuestionText} />
          </Typography>
        </Button>
      </Grid>
      <Grid item xs={12}>
        {activeDataEntries.length === 0 ? (
          <div className={classes.emptyStateContainer}>
            <Typography color="textPrimary">
              <FormattedMessage {...emptyStateHeader} />
            </Typography>
            <Typography variant="body2">
              <FormattedMessage {...emptyStateSubText} />
            </Typography>
            <Button
              type="secondary"
              size="small"
              onClick={setCreateQuestion}
              autoWidth
            >
              <Typography
                variant="body2"
                color="primary"
                className={classes.addQuesitonButtonText}
              >
                <Add className={classes.addIcon} />
                <FormattedMessage {...addQuestionText} />
              </Typography>
            </Button>
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="review-questions">
              {provided => (
                <ul
                  className={classes.questionsContainer}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {activeDataEntries.map((dataField, index) => {
                    const draggableId = String(
                      // eslint-disable-next-line no-underscore-dangle
                      dataField.id ?? dataField._id ?? dataField.key,
                    );
                    return (
                      <Draggable
                        key={draggableId}
                        draggableId={draggableId}
                        index={index}
                        isDragDisabled={!isDraggingEnabled}
                      >
                        {(provided, snapshot) => (
                          <li
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <QuestionnaireDisplay
                              dataField={dataField as unknown as DataField}
                              dragHandleProps={provided.dragHandleProps}
                              isDragging={snapshot.isDragging}
                              isDragDisabled={!isDraggingEnabled}
                            />
                          </li>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Grid>
    </Grid>
  );
};

export const FormBuilder = () => (
  <QuestionDrawerProvider>
    <InnerFormBuilder />
  </QuestionDrawerProvider>
);
